import * as React from 'react';
import VideoTags from '../VideoTags';
import { Tag, Video } from '../../../models';
import './style.css';

export enum VideoExtraInfo {
  PublishedAt = 'publishedAt',
  Views = 'views'
}

export interface VideoInfoProps {
  className?: string;
  extraInfo?: VideoExtraInfo;
  video: Video;
  tags?: Array<Tag>;
}

export interface VideoInfoState {
  /* empty*/
}

const MAX_VISIBLE_TAGS = 3;

export class VideoInfo extends React.Component<VideoInfoProps, VideoInfoState> {
  public static get defaultProps(): Partial<VideoInfoProps> {
    return {
      extraInfo: VideoExtraInfo.Views
    };
  }

  render() {
    const { className, extraInfo, tags, video } = this.props;

    const { rootCategory, duration, publishedAt, views = 0 } = video;
    let categoryText = '';
    if (rootCategory) {
      categoryText = rootCategory.name;
    }

    let durationText = '';
    if (duration) {
      durationText = duration.replace(/^00:/, '');
    }

    let extraInfoText = '';
    switch (extraInfo) {
      case VideoExtraInfo.PublishedAt:
        const dateFormatter = new Intl.DateTimeFormat();
        extraInfoText = dateFormatter.format(publishedAt);
        break;

      case VideoExtraInfo.Views:
        const numberFormatter = new Intl.NumberFormat();
        extraInfoText = `${numberFormatter.format(views)} ${
          views === 1 ? 'view' : 'views'
        }`;
        break;

      default:
      // No-op
    }

    return (
      <div className={this.getClassName(className)}>
        {tags ? this.getTagsList(tags) : null}
        <div className="video-info-title" title={video.name}>
          {video.name}
        </div>
        <div className="video-info-details">
          {categoryText ? (
            <span
              className="video-info-detail video-info-category"
              title={categoryText}
            >
              {categoryText}
            </span>
          ) : null}
          {video.duration ? (
            <span
              className="video-info-detail video-info-duration"
              title={durationText}
            >
              {durationText}
            </span>
          ) : null}
          <span
            className="video-info-detail video-info-extra-info"
            title={extraInfoText}
          >
            {extraInfoText}
          </span>
        </div>
      </div>
    );
  }

  private getClassName = (className?: string): string => {
    return [className, 'video-info']
      .filter((value: string | undefined) => !!value)
      .join(' ');
  };

  private getTagsList = (tags: Array<Tag>): JSX.Element => {
    const tagsToDisplay = this.getTagsToDisplay(tags);

    return (
      <VideoTags
        doNotLinkTags={true}
        tags={tagsToDisplay.tags}
        tooltips={tagsToDisplay.tooltips}
      />
    );
  };

  private getTagsToDisplay = (
    tags: Array<Tag>
  ): {
    tags: Array<Tag>;
    tooltips?: WeakMap<Tag, string>;
  } => {
    if (tags.length <= MAX_VISIBLE_TAGS) {
      return { tags };
    }

    const actualTagsToShow = MAX_VISIBLE_TAGS - 1;
    const { overflowTag, overflowTooltip } = this.getOverflowTagAndTooltip(
      tags.slice(actualTagsToShow)
    );

    const tooltips = new WeakMap();
    tooltips.set(overflowTag, overflowTooltip);

    return {
      tags: [...tags.slice(0, actualTagsToShow), overflowTag],
      tooltips
    };
  };

  private getOverflowTagAndTooltip = (
    overflowTags: Array<Tag>
  ): {
    overflowTag: Tag;
    overflowTooltip: string;
  } => {
    const overflowTag = new Tag(-1, `+ ${overflowTags.length} more`, `#888`);

    const overflowTooltip = this.getOverflowTagsTooltip(overflowTags);

    return { overflowTag, overflowTooltip };
  };

  private getOverflowTagsTooltip = (tags: Array<Tag>): string => {
    return tags.map(({ name }) => name).join('\n');
  };
}

export default VideoInfo;



// WEBPACK FOOTER //
// ./src/app/components/Video/VideoInfo/index.tsx